import { useCallback, useEffect, useState } from 'react';
import type { MaskedSettings } from '@ozon-ai/apigateway/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useTelegram } from '../../../context/TelegramContext';
import { SETTINGS_ACCESS } from '../settings-paths';
import {
  Field,
  SettingsErrorAlert,
  SettingsForm,
  SettingsFormCard,
  SettingsHint,
  SettingsStatusLine,
  SettingsSubPageShell,
  SettingsSuccessAlert,
} from '../settings-shared';

interface ServiceStatus {
  ozon: { ok: boolean; error?: string };
  ozonPerformance?: { ok: boolean; error?: string };
}

export function SettingsOzonPage() {
  const { webApp } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [ozonClientId, setOzonClientId] = useState('');
  const [ozonApiKey, setOzonApiKey] = useState('');
  const [performanceClientId, setPerformanceClientId] = useState('');
  const [performanceApiKey, setPerformanceApiKey] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<MaskedSettings>('/settings');
      if (data) {
        setOzonClientId(data.secrets.ozon.clientId);
        setOzonApiKey(data.secrets.ozon.apiKey);
        setPerformanceClientId(data.secrets.ozon.performanceClientId ?? '');
        setPerformanceApiKey(data.secrets.ozon.performanceApiKey ?? '');
      }
      try {
        const st = await api.get<ServiceStatus>('/settings/status');
        setStatus(st);
      } catch {
        setStatus(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveOzon() {
    setBusy(true);
    setMessage(null);
    try {
      await api.put('/settings/secrets', {
        ozon: {
          clientId: ozonClientId,
          apiKey: ozonApiKey,
          performanceClientId,
          performanceApiKey,
        },
      });
      setMessage('Ozon сохранён');
      webApp?.HapticFeedback?.notificationOccurred('success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <SettingsSubPageShell title="Ozon" backTo={SETTINGS_ACCESS}>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {message && <SettingsSuccessAlert>{message}</SettingsSuccessAlert>}

      <SettingsFormCard>
        <SettingsStatusLine label="Статус" ok={status?.ozon.ok} error={status?.ozon.error} />
      </SettingsFormCard>

      <SettingsFormCard>
        <SettingsStatusLine
          label="Performance"
          ok={status?.ozonPerformance?.ok}
          error={status?.ozonPerformance?.error}
        />
        <SettingsHint className="mt-3">
          Ключи Performance — из кабинета Ozon (Продвижение → Performance API): Client ID (только цифры)
          и Client Secret. Это не пара Seller API (Client-Id / Api-Key). В UI показываются только last4;
          оставьте маску ****xxxx, если не хотите менять значение.
        </SettingsHint>
        <SettingsForm onSubmit={saveOzon} className="mt-4">
          <Field label="Ozon Client ID" id="ozonClientId" value={ozonClientId} onChange={setOzonClientId} />
          <Field label="Ozon API Key" id="ozonApiKey" value={ozonApiKey} onChange={setOzonApiKey} />
          <Field
            label="Performance Client ID (опционально)"
            id="performanceClientId"
            value={performanceClientId}
            onChange={setPerformanceClientId}
          />
          <Field
            label="Performance Client Secret (опционально)"
            id="performanceApiKey"
            value={performanceApiKey}
            onChange={setPerformanceApiKey}
          />
          <Button type="submit" className="w-full" disabled={busy}>
            Сохранить
          </Button>
        </SettingsForm>
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}
